import * as React from 'react';
import Typography from '@mui/material/Typography';
import FlowSyncsTable from './flowSyncsTable/FlowSyncsTable';
import { useGlobalSettings } from '../../app/GlobalSettingProvider';
import { formatTime } from './utils';
import FlowExtractsTable from './flowExtractsTable/FlowExtractsTable';
import { TabLayout, Tab } from './TabLayout';
import { Model } from '../../data/api';
import { BrowserTabTitle } from '../BrowserTabTitle';
import { Flow } from '../../types/flow.types';
import { useAccount } from '../../auth';
import { useEntities } from '../../data/flowEntities';
import { twentyFourHoursAgo } from './utils';
import {
  ModelCollection,
  JobCounts,
  Job,
  FlowModel,
} from '../../types/flow.types';
import FlowHeaderTitleSection from './flowHeaderSections/FlowHeaderTitleSection';
import FlowHeaderSourceSection from './flowHeaderSections/FlowHeaderSourceSection';
import FlowHeaderDestinationSection from './flowHeaderSections/FlowHeaderDestinationSection';
import FlowHeaderSyncRunsSection from './flowHeaderSections/FlowHeaderSyncRunsSection';
import FlowHeaderLastRunSection from './flowHeaderSections/FlowHeaderLastRunSection';
import { EntityDetailCard } from './EntityDetailCard';
import { formatDistance } from 'date-fns';

interface FlowEntityProps {
  flow: Model | Flow | null;
  currentAccount: { id: string; name?: string; title?: string };
  tabIsActive?: boolean;
}

export const FlowEntity: React.FC<FlowEntityProps> = ({
  flow,
  currentAccount,
  tabIsActive,
}) => {
  // Section logic moved from FlowEntityHeader
  const { settings } = useGlobalSettings();
  const { currentAccount: account } = useAccount();
  const flowModel = flow as FlowModel;
  const flowJobsCountsQuery = useEntities(
    account.id,
    'job',
    {
      page: 1,
      size: 10000,
      filter: {
        job_type: 'flow',
        entity_id: flowModel?.id,
        updated_at__gte: settings.datePicker.start,
        updated_at__lte: settings.datePicker.end,
      },
    },
    {
      // refetchInterval: 3000,
      enabled: Boolean(flowModel?.id),
    },
  );
  const percentageOfJobsByStatus = (() => {
    const data = flowJobsCountsQuery?.data;
    if (!data) return {};
    if (
      typeof data === 'object' &&
      data !== null &&
      typeof (data as unknown as ModelCollection).getData === 'function'
    ) {
      return (data as unknown as ModelCollection)
        .getData()
        .reduce((acc: JobCounts, job: Job) => {
          acc[job.status] = acc[job.status] ? acc[job.status] + 1 : 1;
          return acc;
        }, {});
    }
    return {};
  })();

  const metaCount = (() => {
    const data = flowJobsCountsQuery?.data;
    if (!data) return 0;
    if (
      typeof data === 'object' &&
      data !== null &&
      'meta' in data &&
      typeof (data as any).meta?.count === 'number'
    ) {
      return (data as { meta?: { count?: number } }).meta?.count ?? 0;
    }
    return 0;
  })();

  return (
    <div>
      <BrowserTabTitle>{`Flow - ${(flow as Flow)?.name ?? ''}`}</BrowserTabTitle>
      <EntityDetailCard
        data-cy="flow-entity-detail-card"
        status={flow?.['health-status']?.status}
        isLoading={flowJobsCountsQuery.isLoading}
        isSuccess={flowJobsCountsQuery.isSuccess}
      >
        <FlowHeaderTitleSection flow={flow as Flow | FlowModel | null} />
        <FlowHeaderSourceSection flowModel={flowModel} />
        <FlowHeaderDestinationSection flowModel={flowModel} />
        <FlowHeaderSyncRunsSection
          metaCount={metaCount}
          percentageOfJobsByStatus={percentageOfJobsByStatus}
          timezone={settings.timeZone.current}
        />
        <FlowHeaderLastRunSection
          flowModel={flowModel}
          accountName={(account as { id: string; name?: string }).name ?? ''}
          timezone={settings.timeZone.current}
        />
      </EntityDetailCard>
      <TabLayout ariaLabel="flow-detail">
        <Tab
          name="syncs"
          title={({ value, index }) => (
            <>
              <Typography
                variant="body2"
                color="initial"
                fontWeight={value === index ? 900 : 400}
                sx={{
                  marginTop: 0,
                  paddingX: 4,
                  display: 'inline-block',
                  lineHeight: 1,
                }}
              >
                SYNCS
              </Typography>
              <Typography
                variant="overline"
                color="initial"
                sx={{ fontSize: '10px', marginTop: 0, marginBottom: 1 }}
              >
                since{' '}
                {formatDistance(
                  settings.datePicker.start,
                  settings.datePicker.end,
                  {
                    addSuffix: true,
                  },
                )}
              </Typography>
            </>
          )}
        >
          <FlowSyncsTable
            flow={flow as Model}
            currentAccount={currentAccount}
          />
        </Tab>
        <Tab
          name="extracts"
          title={({ value, index }) => (
            <Typography
              variant="body2"
              color="initial"
              fontWeight={value === index ? 900 : 400}
              sx={{
                marginTop: 0,
                paddingX: 4,
                display: 'inline-block',
                lineHeight: 1,
              }}
            >
              EXTRACTS
            </Typography>
          )}
        >
          <FlowExtractsTable
            flow={flow as Model}
            currentAccount={currentAccount}
          />
        </Tab>
      </TabLayout>
    </div>
  );
};
